// Shared permit page (integrator-owned). Composes the feature views and shows an
// action panel for each step the server says the viewer can take now.
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { EligibilityPanel, ErrorBanner, EventTimeline, SignatureLine } from '@/components/evidence'
import { Badge, Banner, Button, Card, cx, EmptyState, LinkButton, PageHeader, SectionTitle, Sheet, Spinner } from '@/components/ui'
import { CYCLE_STATES, PERMIT_STATES } from '@/domain/catalogue'
import { currentOmApproval, omApprovalSentence, omApprovalStatus, OM_STATUS_TEXT, roleDisplayName } from '@/domain/omApproval'
import type { AttestationView, AvailableAction, PermitBundle } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import { usePermit } from '@/lib/queries'
import { ApplicationView, applicationActionPanels } from '@/features/application'
import { CloseoutView, closeoutActionPanels } from '@/features/closeout'
import { CyclesView, cycleActionPanels } from '@/features/cycles'
import type { ActionPanelMap } from '@/features/contracts'
import { extensionSentence } from '@/domain/extension'
import { DecideExtensionPanel, ExtensionCard, RequestExtensionPanel, WithdrawExtensionPanel } from './ExtensionPanels'
import { OmApprovalPanel } from './OmApprovalPanel'

const PANELS: ActionPanelMap = {
  ...applicationActionPanels,
  approve_permit_om: OmApprovalPanel,
  request_extension: RequestExtensionPanel,
  approve_extension: DecideExtensionPanel,
  refuse_extension: DecideExtensionPanel,
  withdraw_extension: WithdrawExtensionPanel,
  ...cycleActionPanels,
  ...closeoutActionPanels,
}

type Tab = 'application' | 'cycles' | 'close' | 'history'

/** A shown copy older than this (longer than the 30-second refresh) is marked while it is re-read. */
const OLD_COPY_MS = 45_000

function allAttestations(b: PermitBundle): AttestationView[] {
  const out: AttestationView[] = []
  const seen = new Set<string>()
  const add = (a: AttestationView | null | undefined) => {
    if (a && !seen.has(a.id)) {
      seen.add(a.id)
      out.push(a)
    }
  }
  b.revisions.forEach((r) => add(r.submission))
  b.reviews.forEach((r) => add(r.attestation))
  b.approvals.forEach((r) => add(r.attestation))
  b.examinations.forEach((r) => add(r.attestation))
  b.supporting_records.forEach((r) => {
    add(r.recorded)
    r.verifications.forEach((v) => add(v.attestation))
  })
  b.authorisations.forEach((r) => add(r.attestation))
  ;(b.om_approvals ?? []).forEach((r) => add(r.attestation))
  ;(b.extension_requests ?? []).forEach((r) => {
    add(r.requested)
    add(r.decision?.attestation)
  })
  b.cycles.forEach((c) => {
    add(c.preissue?.attestation)
    add(c.validation?.attestation)
    c.examinations.forEach((x) => add(x.attestation))
    c.gas_tests.forEach((g) => add(g.attestation))
    c.toolbox_talks.forEach((t) => {
      add(t.giver)
      t.attendees.forEach((x) => add(x.signed))
    })
    add(c.acceptance?.attestation)
    c.suspensions.forEach((s) => {
      add(s.ph_attestation)
      s.confirmations.forEach((x) => add(x.attestation))
    })
  })
  add(b.cancellation?.attestation)
  add(b.closeout?.attestation)
  return out.sort((x, y) => x.server_signed_at.localeCompare(y.server_signed_at))
}

/**
 * The Operations Manager's approval of the current authorisation (0016). Shown only when the
 * permit's policy has the OM step; older permits and policies without it show nothing.
 */
function OmApprovalCard({ bundle }: { bundle: PermitBundle }) {
  const status = omApprovalStatus(bundle)
  if (status === 'not_required') return null
  const approval = currentOmApproval(bundle)
  const t = OM_STATUS_TEXT[status]
  return (
    <Card>
      <SectionTitle aside={<Badge tone={t.tone}>{t.label}</Badge>}>Operations Manager approval</SectionTitle>
      <p className="text-sm text-ink">{omApprovalSentence(bundle, fmtDateTime)}</p>
      {approval ? (
        <div className="mt-2 space-y-2">
          <SignatureLine a={approval.attestation} label="Approved by the Operations Manager" />
          <p className="text-xs text-ink-soft">Declaration signed: {approval.declaration}</p>
          {approval.notes ? <p className="text-sm text-ink-soft">Notes: {approval.notes}</p> : null}
        </div>
      ) : null}
    </Card>
  )
}

/**
 * A role this person can never take on this permit, because they already hold another role on
 * it (the same-permit rules, e.g. RS and PH). Listing it only confuses (Razan, 2026-11-04); the
 * server still refuses it if it were attempted.
 */
export function isSamePermitConflict(a: AvailableAction): boolean {
  return !a.enabled && /^The same person cannot be /.test(a.reason ?? '')
}

function ActionList({ bundle, onOpen }: { bundle: PermitBundle; onOpen: (a: AvailableAction) => void }) {
  const actions = bundle.viewer.available_actions.filter((a) => !isSamePermitConflict(a))
  if (actions.length === 0) {
    return <p className="text-sm text-ink-soft">Nothing for you to do on this permit right now.</p>
  }
  return (
    <ul className="space-y-2">
      {actions.map((a, i) => (
        <li key={`${a.command}-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-white p-3">
          <div>
            <p className="font-semibold text-ink">{a.label}</p>
            <p className="text-xs text-ink-soft">
              as {roleDisplayName(a.role)}
              {!a.enabled && a.reason ? ` - ${a.reason}` : ''}
            </p>
          </div>
          <Button variant={a.enabled ? 'primary' : 'secondary'} onClick={() => onOpen(a)} disabled={!PANELS[a.command]}>
            {a.enabled ? 'Open' : 'View'}
          </Button>
        </li>
      ))}
    </ul>
  )
}

export function PermitDetailPage() {
  const { id } = useParams()
  const q = usePermit(id)
  const [tab, setTab] = useState<Tab>('application')
  const [open, setOpen] = useState<AvailableAction | null>(null)

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!q.data) {
    return <ErrorBanner error={q.error ?? new Error('Permit not available')} />
  }
  const b = q.data
  // A copy kept from an earlier visit is shown at once while the page reads the permit
  // again. Older than the 30-second refresh, it may miss a stop or a signature: say so
  // until the server answers (the routine 30-second refreshes stay silent).
  const checkingOldCopy = q.isFetching && q.dataUpdatedAt > 0 && Date.now() - q.dataUpdatedAt > OLD_COPY_MS
  const st = PERMIT_STATES[b.permit.lifecycle_state] ?? { label: b.permit.lifecycle_state, tone: 'neutral' as const }
  const cycle = b.cycles.find((c) => c.id === b.permit.current_cycle_id) ?? null
  const Panel = open ? PANELS[open.command] : undefined

  return (
    <div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="code">{b.permit.permit_number}</span>
            <Badge tone={b.permit.permit_class === 'B' ? 'info' : 'bad'}>Class {b.permit.permit_class}</Badge>
            {b.permit.is_synthetic ? <Badge tone="synthetic">synthetic</Badge> : null}
          </span>
        }
        subtitle={`${b.permit.title || 'Untitled'} - ${b.permit.site.name}, ${b.permit.site_area.name}`}
        actions={
          <LinkButton to={`/permits/${b.permit.id}/print`} variant="secondary">
            Print
          </LinkButton>
        }
      />

      {q.isError ? (
        <Banner tone="warn" title="Showing the last copy loaded - not current">
          The latest refresh failed. This copy was read at {fmtDateTime(b.generated_at)}; it is not a current authorisation.
          Stop work physically if in doubt and refresh when the connection returns.
        </Banner>
      ) : checkingOldCopy ? (
        <Banner tone="info" title="Checking for changes">
          This copy was read at {fmtDateTime(b.generated_at)}, before you opened the permit. It is being refreshed now; wait for the
          latest copy before relying on its status or next steps.
        </Banner>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <EligibilityPanel e={b.eligibility} />
          <OmApprovalCard bundle={b} />
          <ExtensionCard bundle={b} />
          <Card>
            <SectionTitle aside={<Badge tone={st.tone}>{st.label}</Badge>}>Your next steps</SectionTitle>
            <ActionList bundle={b} onOpen={setOpen} />
          </Card>
        </div>
        <Card>
          <SectionTitle>Status</SectionTitle>
          <ul className="space-y-1 text-sm">
            <li>
              <span className="text-ink-soft">Permit: </span>
              {st.label}
            </li>
            <li>
              <span className="text-ink-soft">Current cycle: </span>
              {cycle ? `#${cycle.cycle_number} ${CYCLE_STATES[cycle.cycle_state]?.label ?? cycle.cycle_state}` : 'none'}
            </li>
            {omApprovalStatus(b) !== 'not_required' ? (
              <li>
                <span className="text-ink-soft">OM approval: </span>
                {omApprovalStatus(b) === 'approved' ? 'approved' : 'pending - no work can start until the OM approves'}
              </li>
            ) : null}
            {b.permit.lifecycle_state === 'authorised' && extensionSentence(b, fmtDateTime) ? (
              <li>
                <span className="text-ink-soft">Authorised period: </span>
                {extensionSentence(b, fmtDateTime)}
              </li>
            ) : null}
            <li>
              <span className="text-ink-soft">Your roles here: </span>
              {b.viewer.roles_on_permit.length ? b.viewer.roles_on_permit.map(roleDisplayName).join(', ') : 'none'}
            </li>
            <li>
              <span className="text-ink-soft">Rules: </span>
              {b.policy.version_label} ({b.policy.status.replace(/_/g, ' ')})
            </li>
            <li className="text-xs text-ink-faint">Version v{b.permit.row_version} - loaded {fmtDateTime(b.generated_at)}</li>
          </ul>
        </Card>
      </div>

      <div className="no-print mt-6 flex gap-1 overflow-x-auto border-b border-line" role="tablist">
        {(
          [
            ['application', 'Application'],
            ['cycles', 'Daily cycles'],
            ['close', 'Stops & closeout'],
            ['history', 'Signatures & history'],
          ] as Array<[Tab, string]>
        ).map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={cx('whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold', tab === k ? 'border-brand text-brand-dark' : 'border-transparent text-ink-soft')}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mt-4">
        {tab === 'application' ? <ApplicationView bundle={b} /> : null}
        {tab === 'cycles' ? <CyclesView bundle={b} /> : null}
        {tab === 'close' ? <CloseoutView bundle={b} /> : null}
        {tab === 'history' ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <SectionTitle>Signatures</SectionTitle>
              {allAttestations(b).length === 0 ? (
                <EmptyState title="No signatures yet" />
              ) : (
                <div className="space-y-2">
                  {allAttestations(b).map((a) => (
                    <SignatureLine key={a.id} a={a} label={a.declared_capacity.replace(/_/g, ' ')} />
                  ))}
                </div>
              )}
            </Card>
            <Card>
              <SectionTitle>Event log</SectionTitle>
              <EventTimeline events={b.events} />
            </Card>
          </div>
        ) : null}
      </div>

      <Sheet open={Boolean(open)} title={open?.label ?? ''} onClose={() => setOpen(null)}>
        {open && Panel ? (
          <Panel
            bundle={b}
            action={open}
            onCancel={() => setOpen(null)}
            onDone={() => {
              setOpen(null)
              void q.refetch()
            }}
          />
        ) : null}
      </Sheet>
    </div>
  )
}
